import { countryCodes } from "@/constants/countries";
import { fonts } from "@/constants/fonts";
import { useThemeColor } from "@/hooks/useThemeColor";
import { Ionicons } from "@expo/vector-icons";
import { BottomSheetFlatList } from "@gorhom/bottom-sheet";
import React, { useRef, useState } from "react";
import {
  TextInput as RNTextInput,
  TextInputProps as RNTextInputProps,
  StyleSheet,
  TextStyle,
  TouchableOpacity,
  View,
  ViewStyle,
} from "react-native";
import AppBottomSheetModal, {
  AppBottomSheetModalRef,
} from "../modals/AppBottomSheetModal";
import ThemedText from "../core/ThemedText";

type InputVariant = "default" | "filled" | "outlined" | "ghost";
type InputSize = "sm" | "md" | "lg";

interface TextInputProps extends Omit<RNTextInputProps, "style"> {
  label?: string;
  error?: string;
  variant?: InputVariant;
  size?: InputSize;
  containerStyle?: ViewStyle;
  inputStyle?: TextStyle;
  disabled?: boolean;
  isPassword?: boolean;
  showCharacterCount?: boolean;
  helperText?: string;
}

export const TextInput: React.FC<TextInputProps> = ({
  label,
  error,
  variant = "default",
  size = "md",
  containerStyle,
  inputStyle,
  disabled = false,
  isPassword = false,
  showCharacterCount = false,
  helperText,
  ...props
}) => {
  const backgroundColor = useThemeColor({}, "backgroundTertiary");
  const textColor = useThemeColor({}, "text");
  const borderColor = useThemeColor({}, "border");
  const placeholderColor = useThemeColor({}, "textTertiary");
  const primaryColor = useThemeColor({}, "primary");
  const errorColor = useThemeColor({}, "error");
  const disabledTextColor = useThemeColor({}, "textDisabled");
  const disabledBackgroundColor = useThemeColor({}, "backgroundDisabled");
  const [showPassword, setShowPassword] = useState(isPassword);
  const [selectedCountryCode, setSelectedCountryCode] = useState("+216");
  const [searchQuery, setSearchQuery] = useState("");
  const bottomSheetRef = useRef<AppBottomSheetModalRef>(null);

  const sizeStyles: Record<
    InputSize,
    { height?: number; fontSize: number; padding: number }
  > = {
    sm: { fontSize: 16, padding: 8 },
    md: { height: 50, fontSize: 16, padding: 14 },
    lg: { height: 55, fontSize: 32, padding: 16 },
  };

  const isPhoneInput = props.keyboardType === "phone-pad";

  const getVariantStyle = () => {
    const baseStyle: ViewStyle = {
      borderRadius: 12,
      position: "relative",
      backgroundColor,
    };

    switch (variant) {
      case "filled":
        return {
          ...baseStyle,
          backgroundColor: disabled ? disabledBackgroundColor : backgroundColor,
        };
      case "outlined":
        return {
          ...baseStyle,
          backgroundColor: "transparent",
          borderWidth: 1,
          borderColor: error ? errorColor : borderColor,
        };
      case "ghost":
        return {
          ...baseStyle,
          backgroundColor: "transparent",
        };
      default:
        return baseStyle;
    }
  };

  const getTextColor = () => {
    if (disabled) {
      return disabledTextColor;
    }
    return textColor;
  };

  const handleCountryCodeSelect = (code: string) => {
    setSelectedCountryCode(code);
    setSearchQuery("");
    bottomSheetRef.current?.dismiss();
  };

  const filteredCountries = countryCodes.filter((country) => {
    const query = searchQuery.toLowerCase();
    return (
      country.name.toLowerCase().includes(query) ||
      country.dial_code.includes(query) ||
      country.code.toLowerCase().includes(query)
    );
  });

  return (
    <View style={[styles.container, containerStyle]}>
      {label && (
        <ThemedText
          style={[styles.label, disabled && { color: disabledTextColor }]}
        >
          {label}
        </ThemedText>
      )}
      <View style={[getVariantStyle(), disabled && styles.disabled]}>
        {isPhoneInput && (
          <TouchableOpacity
            onPress={() => bottomSheetRef.current?.present()}
            style={[
              styles.countryCodeContainer,
              { borderRightColor: borderColor },
            ]}
            disabled={disabled}
          >
            <ThemedText style={styles.countryCodeText}>
              {selectedCountryCode}
            </ThemedText>
          </TouchableOpacity>
        )}
        <RNTextInput
          style={[
            {
              height: sizeStyles[size].height,
              fontSize: sizeStyles[size].fontSize,
              padding: sizeStyles[size].padding,
              color: getTextColor(),
              paddingLeft: isPhoneInput ? 70 : sizeStyles[size].padding,
            },
            inputStyle,
          ]}
          secureTextEntry={showPassword}
          placeholderTextColor={placeholderColor}
          editable={!disabled}
          {...props}
        />
        {isPassword && (
          <TouchableOpacity
            onPress={() => {
              setShowPassword(!showPassword);
            }}
            style={styles.iconContainer}
          >
            {showPassword ? (
              <Ionicons name="eye-outline" color={textColor} size={16} />
            ) : (
              <Ionicons name="eye-off-outline" color={textColor} size={16} />
            )}
          </TouchableOpacity>
        )}
      </View>
      {error && (
        <ThemedText style={[styles.error, { color: errorColor }]}>
          {error}
        </ThemedText>
      )}

      {/* Helper text or character count */}
      {(helperText || showCharacterCount) && !error && (
        <View style={styles.helperContainer}>
          {helperText && (
            <ThemedText style={[styles.helperText, { color: placeholderColor }]}>
              {helperText}
            </ThemedText>
          )}
          {showCharacterCount && props.maxLength && (
            <ThemedText style={[styles.characterCount, { color: placeholderColor }]}>
              {(props.value?.toString() || "").length}/{props.maxLength}
            </ThemedText>
          )}
        </View>
      )}

      <AppBottomSheetModal
        ref={bottomSheetRef}
        snapPoints={["75%", "90%"]}
        enablePanDownToClose
      >
        <View style={styles.bottomSheetContent}>
          <ThemedText style={styles.bottomSheetTitle}>
            Select Country Code
          </ThemedText>

          {/* Search Input */}
          <View style={[styles.searchContainer, { backgroundColor, borderColor }]}>
            <Ionicons
              name="search"
              size={20}
              color={placeholderColor}
              style={styles.searchIcon}
            />
            <RNTextInput
              style={[
                styles.searchInput,
                {
                  color: textColor,
                },
              ]}
              placeholder="Search country..."
              placeholderTextColor={placeholderColor}
              value={searchQuery}
              onChangeText={setSearchQuery}
              autoCapitalize="none"
              autoCorrect={false}
            />
            {searchQuery.length > 0 && (
              <TouchableOpacity onPress={() => setSearchQuery("")}>
                <Ionicons name="close-circle" size={20} color={placeholderColor} />
              </TouchableOpacity>
            )}
          </View>

          {/* Results count */}
          {searchQuery.length > 0 && (
            <ThemedText style={styles.resultsCount}>
              {filteredCountries.length} {filteredCountries.length === 1 ? "result" : "results"}
            </ThemedText>
          )}

          {/* Country List */}
          <BottomSheetFlatList
            data={filteredCountries}
            keyExtractor={(item: any) => item.code}
            renderItem={({ item }: any) => (
              <TouchableOpacity
                style={[
                  styles.countryItem,
                  { borderBottomColor: borderColor },
                  selectedCountryCode === item.dial_code && {
                    backgroundColor: primaryColor + "10",
                  },
                ]}
                onPress={() => handleCountryCodeSelect(item.dial_code)}
              >
                <View style={styles.countryItemContent}>
                  <ThemedText style={styles.countryFlag}>{item.flag}</ThemedText>
                  <View style={styles.countryTextContainer}>
                    <ThemedText style={styles.countryNameText}>{item.name}</ThemedText>
                    <ThemedText style={[styles.countryCode, { color: placeholderColor }]}>
                      {item.code}
                    </ThemedText>
                  </View>
                  <ThemedText style={styles.dialCode}>{item.dial_code}</ThemedText>
                </View>
                {selectedCountryCode === item.dial_code && (
                  <Ionicons name="checkmark-circle" size={20} color={primaryColor} />
                )}
              </TouchableOpacity>
            )}
            showsVerticalScrollIndicator={true}
            ListEmptyComponent={
              <View style={styles.emptyState}>
                <Ionicons name="search-outline" size={48} color={placeholderColor} />
                <ThemedText style={[styles.emptyText, { color: placeholderColor }]}>
                  No countries found
                </ThemedText>
                <ThemedText style={[styles.emptySubtext, { color: placeholderColor }]}>
                  Try a different search term
                </ThemedText>
              </View>
            }
          />
        </View>
      </AppBottomSheetModal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginBottom: 16,
  },
  label: {
    marginBottom: 4,
    fontSize: 14,
    fontFamily: fonts.medium,
  },
  error: {
    marginTop: 4,
    fontSize: 12,
  },
  helperContainer: {
    marginTop: 4,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  helperText: {
    fontSize: 12,
    flex: 1,
  },
  characterCount: {
    fontSize: 12,
    marginLeft: 8,
  },
  disabled: {
    opacity: 0.5,
  },
  iconContainer: {
    position: "absolute",
    right: 16,
    bottom: "25%",
    top: "35%",
  },
  countryCodeContainer: {
    position: "absolute",
    left: 16,
    top: 0,
    bottom: 0,
    justifyContent: "center",
    zIndex: 1,
    borderRightWidth: 1,

    paddingRight: 10,
  },
  countryCodeText: {
    fontSize: 16,
  },
  bottomSheetContent: {
    flex: 1,
    paddingHorizontal: 20,
    paddingTop: 20,
  },
  bottomSheetTitle: {
    fontSize: 18,
    fontFamily: fonts.bold,
    marginBottom: 16,
    textAlign: "center",
  },
  searchContainer: {
    flexDirection: "row",
    alignItems: "center",
    borderRadius: 12,
    paddingHorizontal: 12,
    paddingVertical: 10,
    marginBottom: 8,
    borderWidth: 1,
  },
  searchIcon: {
    marginRight: 8,
  },
  searchInput: {
    flex: 1,
    fontSize: 16,
    fontFamily: fonts.regular,
    padding: 0,
  },
  resultsCount: {
    fontSize: 12,
    opacity: 0.6,
    marginBottom: 12,
    textAlign: "center",
  },
  countryItem: {
    paddingVertical: 14,
    paddingHorizontal: 12,
    borderBottomWidth: 1,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    borderRadius: 8,
  },
  countryItemContent: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
  },
  countryFlag: {
    fontSize: 24,
    marginRight: 12,
  },
  countryTextContainer: {
    flex: 1,
  },
  countryNameText: {
    fontSize: 16,
    fontFamily: fonts.medium,
    marginBottom: 2,
  },
  countryCode: {
    fontSize: 12,
    opacity: 0.6,
  },
  dialCode: {
    fontSize: 16,
    fontFamily: fonts.semiBold,
    marginRight: 12,
  },
  emptyState: {
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 48,
  },
  emptyText: {
    fontSize: 16,
    fontFamily: fonts.semiBold,
    marginTop: 16,
  },
  emptySubtext: {
    fontSize: 14,
    marginTop: 8,
  },
});

export default TextInput;
